import type { ResponseConfig } from "client";
import type { FormikHelpers, FormikValues } from "formik";
import { useState } from "react";

export interface UseEntityFormOptions<TValues, TResponse> {
  /** Perform the create/update for the submitted form values. */
  mutate: (values: TValues) => Promise<ResponseConfig<TResponse>>;
  /** Called after a 2xx response — the form has already been reset. */
  onSuccess?: (response: ResponseConfig<TResponse>) => void | Promise<void>;
}

export interface UseEntityFormResult<TValues> {
  error?: Error;
  onSubmit: (values: TValues, helpers: FormikHelpers<TValues>) => Promise<void>;
}

/**
 * Owns the Formik submit boilerplate that ~create forms repeat: toggle
 * `submitting`, run the mutation, branch on the HTTP status (reset + `onSuccess`
 * on 2xx, surface `statusText`/thrown errors otherwise). Pair with
 * {@link EntityForm}; the caller supplies how to mutate and what to do on
 * success (e.g. navigate).
 */
export function useEntityForm<TValues extends FormikValues, TResponse>(
  options: UseEntityFormOptions<TValues, TResponse>,
): UseEntityFormResult<TValues> {
  const [error, setError] = useState<Error | undefined>(undefined);

  const onSubmit = async (
    values: TValues,
    { setSubmitting, resetForm }: FormikHelpers<TValues>,
  ): Promise<void> => {
    setSubmitting(true);
    setError(undefined);
    try {
      const response = await options.mutate(values);
      if (response.status >= 200 && response.status < 300) {
        await resetForm({});
        await options.onSuccess?.(response);
      } else {
        setError(new Error(response.statusText));
      }
    } catch (err) {
      setError(err as Error);
    }
    setSubmitting(false);
  };

  return { error, onSubmit };
}
